import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';

const PaymentRequests = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState({});

  useEffect(() => {
    fetchPaymentRequests();
  }, []);

  const fetchPaymentRequests = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer/payment-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const requestsData = await response.json();
        setPaymentRequests(requestsData);
      }
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      toast({
        title: "Error",
        description: "Failed to load payment requests.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (requestId) => {
    setProcessingPayment({...processingPayment, [requestId]: true});

    try {
      const token = localStorage.getItem('dcTech_token');
      
      // Create payment session
      const paymentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderType: 'service',
          orderId: requestId,
          hostUrl: window.location.origin
        }),
      });

      if (!paymentResponse.ok) {
        throw new Error('Failed to create payment session');
      }

      const paymentData = await paymentResponse.json();

      // Redirect to Stripe Checkout
      window.location.href = paymentData.url;

    } catch (error) {
      console.error('Error creating payment:', error);
      toast({
        title: "Payment failed",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPayment({...processingPayment, [requestId]: false});
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading payment requests...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/profile">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Profile
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Payment Requests</h1>
          <p className="text-xl text-white/70">
            Complete payment for approved service bookings
          </p>
        </div>

        {paymentRequests.length === 0 ? (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-6">💳</div>
              <h2 className="text-2xl font-bold text-white mb-4">No payment requests</h2>
              <p className="text-white/70 mb-6">
                When your service bookings are approved by providers, payment requests will appear here.
              </p>
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                  Book Services
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {paymentRequests.map(request => (
              <Card key={request._id} className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-white text-xl">
                      Service Booking Approved
                    </CardTitle>
                    <Badge className="bg-green-500 text-white">
                      Ready for Payment
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Service Details */}
                    <div className="bg-white/5 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">Service Details</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-white/70">Service:</span>
                          <span className="text-white">{request.items[0]?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Type:</span>
                          <span className="text-white">{request.serviceType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Date:</span>
                          <span className="text-white">
                            {request.scheduledDate ? new Date(request.scheduledDate).toLocaleDateString() : 'Not set'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/70">Time:</span>
                          <span className="text-white">{request.scheduledTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Provider Info */}
                    <div className="bg-white/5 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">Provider Details</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-white/70">Company:</span>
                          <span className="text-white">{request.provider?.company || request.provider?.name}</span>
                        </div>
                        {request.provider?.phone && (
                          <div className="flex justify-between">
                            <span className="text-white/70">Phone:</span>
                            <span className="text-white">{request.provider?.phone}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-white/70">Status:</span>
                          <span className="text-green-400">Approved</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {request.notes && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-2">Your Notes</h4>
                      <p className="text-white/80">{request.notes}</p>
                    </div>
                  )}

                  {/* Payment Summary */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3">Payment Summary</h4>
                    <div className="flex justify-between items-center text-xl">
                      <span className="text-white">Total Amount:</span>
                      <span className="text-green-400 font-bold">${request.total}</span>
                    </div>
                    <p className="text-white/60 text-sm mt-2">
                      Approved on {new Date(request.approvedAt || request.updatedAt).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Payment Action */}
                  <div className="flex gap-4 pt-4">
                    <Button 
                      onClick={() => handlePayment(request._id)}
                      disabled={processingPayment[request._id]}
                      className="flex-1 bg-gradient-to-r from-green-500 to-blue-500   text-white text-lg py-6"
                    >
                      {processingPayment[request._id] ? "Processing..." : "💳 Pay Now - $" + request.total}
                    </Button>
                    <Link to="/profile" className="flex-1">
                      <Button 
                        variant="outline" 
                        className="w-full border-white/50 text-white bg-white/10 text-lg py-6"
                      >
                        View in Profile
                      </Button>
                    </Link>
                  </div>

                  <div className="text-center text-sm text-white/60">
                    🔒 Secure payment powered by Stripe
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRequests;